/**
 * Status Command
 * Show CLI client configuration and installed components status
 *
 * Renamed from 'info' to 'status' for clarity (common CLI pattern)
 */
import { Command } from 'commander';
import chalk from 'chalk';
import boxen from 'boxen';
import { ConfigService } from '../services/config.service.js';
import { ComponentsService } from '../services/components.service.js';
import { ValidationError } from '../errors/index.js';
import { promises as fs } from 'fs';
import { join } from 'path';
import { homedir } from 'os';
import type { InfoOptions } from '../types/framework.types.js';
import { getCliName } from '../utils/cli-name.js';

/**
 * Create status command
 */
export function createStatusCommand(): Command {
  const cmd = new Command('status')
    .description('Hiển thị trạng thái cấu hình và components')
    .option('--json', 'Output as JSON')
    .option('--verbose', 'Show detailed information')
    .action(async (options: InfoOptions) => {
      await handleStatus(options);
    });

  return cmd;
}

async function handleStatus(options: InfoOptions): Promise<void> {
  const configService = new ConfigService();
  const config = await configService.load();

  if (!config) {
    throw new ValidationError(`Not initialized. Run "${getCliName()} auth" first.`);
  }

  const componentsService = new ComponentsService();
  const frameworkPath = join(homedir(), '.jai1', 'framework');
  const projectStatus = await getProjectStatus();
  const installedComponents = await componentsService.getInstalled();
  const componentCount = Object.keys(installedComponents).length;

  const info = {
    configPath: configService.getConfigPath(),
    apiUrl: config.apiUrl,
    accessKey: maskKey(config.accessKey),
    version: config.version || 'Not downloaded',
    lastUpdated: config.lastUpdated || 'Never',
    frameworkPath,
    projectStatus,
    componentCount,
  };

  if (options.json) {
    console.log(JSON.stringify(info, null, 2));
    return;
  }

  // Header
  console.log(
    boxen(chalk.cyan.bold('📊 Jai1 Client Status'), {
      padding: { left: 1, right: 1, top: 0, bottom: 0 },
      borderStyle: 'round',
      borderColor: 'cyan',
    })
  );

  // Configuration section
  console.log(chalk.bold('\n🔧 Cấu hình'));
  console.log(`   ${chalk.dim('Config:')}     ${info.configPath}`);
  console.log(`   ${chalk.dim('API URL:')}    ${info.apiUrl}`);
  console.log(`   ${chalk.dim('Access Key:')} ${chalk.yellow(info.accessKey)}`);

  // Framework section
  console.log(chalk.bold('\n📦 Framework'));
  console.log(`   ${chalk.dim('Version:')}    ${chalk.green(info.version)}`);
  console.log(`   ${chalk.dim('Updated:')}    ${info.lastUpdated}`);
  console.log(`   ${chalk.dim('Location:')}   ${info.frameworkPath}`);

  // Project section
  console.log(chalk.bold('\n📁 Project'));
  const existsIcon = info.projectStatus.exists
    ? chalk.green('✓')
    : chalk.red('✗');
  console.log(`   ${chalk.dim('.jai1/ exists:')}   ${existsIcon}`);
  if (info.projectStatus.exists) {
    console.log(
      `   ${chalk.dim('Components:')}      ${chalk.cyan(componentCount)} installed`
    );
  }

  // Quick tips
  console.log();
  if (!info.projectStatus.exists) {
    console.log(
      chalk.dim(`💡 Chạy "${getCliName()} apply" để cài components cho project này.`)
    );
  } else if (componentCount > 0) {
    console.log(
      chalk.dim(`💡 Chạy "${getCliName()} check" để kiểm tra cập nhật components.`)
    );
  }
}

function maskKey(key: string): string {
  if (key.length <= 4) return '****';
  return '****' + key.slice(-4);
}

async function getProjectStatus(): Promise<{ exists: boolean; version?: string }> {
  const projectJai1 = join(process.cwd(), '.jai1');
  try {
    await fs.access(projectJai1);
    // Try to read version from project
    return { exists: true, version: 'Synced' };
  } catch {
    return { exists: false };
  }
}
